import type { CalendarDate } from '@internationalized/date';
import {
  CalendarStateContext,
  RangeCalendarStateContext
} from 'react-aria-components';
import { ReactElement, useContext } from 'react';
import { useDateFormatter } from 'react-aria';
import { Select } from '../../base/select/select';
import { SelectItem } from '../../base/select/select-item';

type YearItem = {
  id: string;
  date: CalendarDate;
  label: string;
};

export function YearDropdown(): ReactElement {
  const calendarState = useContext(CalendarStateContext);
  const rangeCalendarState = useContext(RangeCalendarStateContext);
  const state = calendarState || rangeCalendarState!;
  const formatter = useDateFormatter({
    year: 'numeric',
    timeZone: state.timeZone
  });

  const maxYear = state.maxValue?.year || new Date().getFullYear() + 20;
  const minYear = state.minValue?.year || new Date().getFullYear() - 100;

  // Format 20 years on each side of the current year according
  // to the current locale and calendar system.
  const years: YearItem[] = [];
  for (
    let i = minYear - state.focusedDate.year;
    i <= maxYear - state.focusedDate.year;
    i++
  ) {
    const date = state.focusedDate.add({ years: i });
    years.push({
      // Use the index as the id so we can retrieve the full
      // date object from the list in onChange. We cannot only
      // store the year number, because in some calendars, such
      // as the Japanese, the era may also change.
      id: String(years.length),
      date,
      label: formatter.format(date.toDate(state.timeZone))
    });
  }

  return (
    <Select
      className="flex-1"
      aria-label="Year"
      style={{ flex: 1, width: 'fit-content' }}
      value={
        years.find((year) => String(state.focusedDate.year) === year.label)?.id
      }
      onChange={(key) => {
        if (key) {
          const year = years[key as number];
          if (year) {
            state.setFocusedDate(year.date);
          }
        }
      }}
      items={years}
    >
      {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
    </Select>
  );
}
